'use client';

/**
 * ObeyaCostPlanVersionsCard — save & compare versions bar for a cost plan.
 *
 * A `Rev` is a named, timestamped tag over the plan's current per-line
 * values — the git-annotated-tag concept, not a "Baseline" vs. "Scenario"
 * split (see klyde_edge/docs/mcp_engine_concept.md, "Identity, values, and
 * authority").
 *
 * This card's two `<select>`s set `compareRevId`/`compareAgainstRevId`
 * on `useCostPlanCompareStore` — it doesn't fetch review data itself at
 * all; `McpWatchWindowCard`'s two-column totals read the same store
 * (room-by-room, both sides, always visible, never clipped by this
 * row's own wrapping) and OWNS the "Review changes" trigger
 * that opens the Concertina (`obeya_costplan_review_header` /
 * `obeya_costplan_review_groups`) — tried moving that trigger onto
 * this row too (co-located with the pickers), reverted: Watch is
 * always visible regardless of canvas tab, this row isn't (it scrolls
 * with the canvas), and having it in both places was two buttons for
 * one action with no space saved either way. "List of versions" (this
 * card, now really "pick a pair to compare") and "diff against one"
 * (the Concertina) stay genuinely separate concepts (mental move 1);
 * rev_diff covers both per-line and selection-level changes (see
 * docs/mcp_scenarios_diff.md — the plan_changes/margin-fx tier is
 * still the one gap, not blocking this).
 *
 * `published_label` ("Rev A", "Rev B", ...) rides the `mcp_rev_list`
 * response pre-formatted (klyde_intelligence/projects/queries.py,
 * list_costplan_revs) — this component renders it verbatim, per the BFF
 * discipline this codebase follows: no client-side ordinal math, no
 * reformatting. `name` is optional free text a person typed; if blank,
 * the badge/panel just show the published label alone.
 *
 * The picker (real UX feedback, resolved here — replaces an earlier
 * panel-of-rows redesign entirely): comparing Scenario-vs-Scenario used
 * to mean picking a base Rev from a panel (always defaulting to "vs
 * Current"), THEN opening the Concertina overlay to find a second "vs"
 * dropdown buried in its header just to change the other side. Both
 * pickers now sit on this one row, always visible — `compareRevId`
 * (left, must be a real tagged Rev — mcp_rev_diff's rev_sid param
 * can't be "current") and `compareAgainstRevId` (right, "Current
 * (live)" or another Rev — mcp_rev_diff's compare_rev_sid). The
 * Concertina's own header (costplan-review-header-card.tsx) no longer
 * carries an editable "vs" control — this row is the one place that
 * changes it now, not two controls doing the same job.
 *
 * `identityLine()` renders what a version IS (published_label + kind,
 * only when kind isn't already implied by the label — "Rev A ·
 * baseline" but just "Scenario 6") for each `<option>`. The pair's own
 * delta reading used to live here too (`useMcpRevDiff`'s `total_impact`/
 * `changed_count`) — removed: Watch's comparing view already shows the
 * full room-by-room breakdown for both sides, which says strictly more
 * than one net number, in a place that doesn't risk this row's own
 * wrapping/clipping at real canvas width.
 *
 * All button/tooltip/empty-state COPY also rides the same response as
 * `labels` (ui_config/handlers/obeya.py, _obeya_labels) — no hardcoded
 * English in this file, same "chrome ships from the BFF" discipline the
 * sibling KPI card already follows (costplan-kpi-row-card.tsx's `L`).
 *
 * @see klyde_edge/klyde_intelligence/projects/handler.py — mcp_tag_rev / mcp_rev_list
 * @see klyde_edge/ui_config/handlers/obeya.py — _obeya_labels (en/zh label source)
 * @see klyde_ipe/components/canvas/cards/obeya/costplan-kpi-row-card.tsx — sibling card, same preset
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo, useState } from 'react';
import { Flag, FlaskConical, FileSpreadsheet, FileType } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { useMcpTagRev, useMcpRevList, type McpRev } from '@/lib/api/hooks';
import { useMcpActions } from '@/lib/hooks/use-mcp-actions';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCostPlanCompareStore } from '@/lib/stores/costplan-compare-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useAffordance } from '@/lib/stores/total-matrix-store';

// Sentinel for the "against" select's real, selectable "Current (live)"
// option — Radix Select disallows an empty-string item value, so the
// null-meaning-current semantic (unchanged from the native <select>'s
// value="") is mapped through this constant at the UI boundary only.
const CURRENT_SENTINEL = '__current__';

function revLine(rev: McpRev): string {
  return rev.name ? `${rev.published_label} — ${rev.name}` : rev.published_label;
}

// The panel row's IDENTITY line — what the version IS (published_label
// + kind, when kind isn't already implied by the label itself), never
// what it's ABOUT. `kind` is only spelled out for a baseline row
// ("Rev A · baseline") — a scenario row's own published_label
// ("Scenario 6") already says what kind it is, so repeating the word
// would be the same redundant-signal mistake the delta line's rework
// (below) exists to fix. `name` (real UX feedback: "— Automated
// verification…" is provenance, not identity — every row was reading
// as the same string, differing only by a truncated timestamp) moved
// to its own de-emphasized line under the delta, not dropped.
function identityLine(rev: McpRev, L: Record<string, string>): string {
  return rev.kind === 'baseline' && L.version_kind_baseline
    ? `${rev.published_label} · ${L.version_kind_baseline}`
    : rev.published_label;
}

export function ObeyaCostPlanVersionsCard() {
  const { mcpId, mcpName } = useViewportSelection();
  const { contentKey } = useCardIdentity();
  const lang = useLanguageStore((s) => s.lang);
  // mcp_document (PDF) / mcp_schedule — Jinja2/WeasyPrint, scheduled for
  // deprecation (docs/wip.md). Only schedule_typst + schedule_xlsx get
  // new export work, so those are the only two download actions here.
  const { downloadMcpScheduleTypst, downloadMcpScheduleXlsx } = useMcpActions();

  const listStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'rev_list'));
  const tagStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'tag_rev'));

  const { data: raw, isLoading } = useMcpRevList(listStrategy, mcpId ?? null, lang ?? undefined);
  const revs = raw?.data?.revs ?? [];
  const latest = revs[0];
  // Server-driven labels — same pattern as the KPI card's `L`. Falls back
  // to '' (never a hardcoded English literal) while the BFF hasn't
  // responded yet — same discipline the KPI card's `label={L.x ?? ''}`
  // already follows; no chrome invented on this side of the wire.
  const L = raw?.data?.labels ?? {};

  const tagRev = useMcpTagRev(tagStrategy ?? '');
  // Two distinct actions, one call, split by `kind` — "Save as new
  // baseline" (kind: 'baseline') moves the plan's active reference and
  // counts toward Rev A/B/C...; "Save as scenario" (kind: 'scenario')
  // tags the same way but never becomes current, counting toward its
  // own separate Scenario 1/2/3... sequence instead. Not a checkbox:
  // picking which one is the point, not an afterthought on one button.
  const [pendingAction, setPendingAction] = useState<'baseline' | 'scenario' | null>(null);
  const [nameDraft, setNameDraft] = useState('');

  const saveAsBaselineTestId = useAffordance('canvas.costplan_versions.save_as_baseline_trigger');
  const saveAsScenarioTestId = useAffordance('canvas.costplan_versions.save_as_scenario_trigger');
  const confirmSaveTestId = useAffordance('canvas.costplan_versions.confirm_save');
  const cancelSaveTestId = useAffordance('canvas.costplan_versions.cancel_save');

  const compareRevId = useCostPlanCompareStore((s) => s.compareRevId);
  const compareMcpId = useCostPlanCompareStore((s) => s.compareMcpId);
  const compareAgainstRevId = useCostPlanCompareStore((s) => s.compareAgainstRevId);
  const setCompareRevId = useCostPlanCompareStore((s) => s.setCompareRevId);
  const setCompareAgainstRevId = useCostPlanCompareStore((s) => s.setCompareAgainstRevId);
  // Same stale-plan guard every compare-reading card here uses — a pick
  // made on a plan the canvas has since navigated away from reads as
  // "not comparing" here too.
  const effectiveRevId = compareMcpId && mcpId && compareMcpId === mcpId ? compareRevId : null;
  const effectiveAgainstRevId = effectiveRevId ? compareAgainstRevId : null;

  const queryKey = ['strategy-data', listStrategy, { semantic_id: mcpId ?? '', ...(lang ? { lang } : {}) }] as const;
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_versions',
    status: (raw ? 'loaded' : mcpId && listStrategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: mcpId && listStrategy ? queryKey : null,
    text: latest ? `Latest saved version: ${revLine(latest)}` : (L.no_versions_saved ?? ''),
    json: revs.length ? { revs: revs.map((r) => ({ published_label: r.published_label, name: r.name, is_current: r.is_current })) } : null,
  }), [contentKey, raw, mcpId, listStrategy, queryKey, latest, revs, L.no_versions_saved]));

  if (!mcpId) {
    return (
      <div className="flex items-center h-full text-xs text-muted-foreground px-1">
        {L.select_cost_plan ?? ''}
      </div>
    );
  }

  const handleSave = () => {
    if (!mcpId || !tagStrategy || !pendingAction) return;
    tagRev.mutate(
      {
        semantic_id: mcpId,
        kind: pendingAction,
        ...(nameDraft.trim() ? { name: nameDraft.trim() } : {}),
      },
      { onSuccess: () => { setPendingAction(null); setNameDraft(''); } },
    );
  };

  const againstOptions = revs.filter((r) => r.rev_sid !== effectiveRevId);

  return (
    <div className="flex items-center gap-2 h-full px-1 flex-wrap">
      {pendingAction ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {pendingAction === 'baseline' ? (L.save_as_baseline ?? '') : (L.save_as_scenario ?? '')}:
          </span>
          <input
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') { setPendingAction(null); setNameDraft(''); }
            }}
            placeholder={L.version_name_placeholder ?? ''}
            className="h-8 px-2 text-xs rounded-md border border-input bg-background w-64"
          />
          <Button data-testid={confirmSaveTestId} size="sm" onClick={handleSave} disabled={tagRev.isPending}>
            {tagRev.isPending ? (L.version_saving ?? '') : (L.version_confirm ?? '')}
          </Button>
          <Button data-testid={cancelSaveTestId} size="sm" variant="ghost" onClick={() => { setPendingAction(null); setNameDraft(''); }}>
            {L.version_cancel ?? ''}
          </Button>
        </div>
      ) : (
        <>
          <Button data-testid={saveAsBaselineTestId} size="sm" variant="outline" onClick={() => setPendingAction('baseline')} disabled={!tagStrategy}
            title={L.save_as_baseline_tooltip ?? ''}>
            <Flag className="w-3.5 h-3.5" />
            {L.save_as_baseline ?? ''}
          </Button>
          {/* Same variant as Save baseline (was `ghost`) — the two save
              actions are equal-weight alternatives (which kind, not a
              primary/secondary pair), so they should look equal-weight. */}
          <Button data-testid={saveAsScenarioTestId} size="sm" variant="outline" onClick={() => setPendingAction('scenario')} disabled={!tagStrategy}
            title={L.save_as_scenario_tooltip ?? ''}>
            <FlaskConical className="w-3.5 h-3.5" />
            {L.save_as_scenario ?? ''}
          </Button>
          {/* Unconditional, same as Save baseline/scenario — the mock's
              own toolbar has Save baseline / Save scenario / exports all
              in one always-visible row, never gated behind a compare
              state. revSid is genuinely optional (confirmed in
              use-mcp-actions.ts): omitted, it exports the current live
              plan — exactly what the composer's own XLSX button already
              does with no rev selected. Only the two non-deprecated
              formats (schedule_typst, schedule_xlsx) get a button —
              mcp_document (PDF) / mcp_schedule are scheduled for
              deprecation, dropped rather than carried along. */}
          <Button size="sm" variant="outline" onClick={() => void downloadMcpScheduleXlsx(mcpId!, mcpName, effectiveRevId)}
            title={L.export_xlsx ?? ''}>
            <FileSpreadsheet className="w-3.5 h-3.5" />
            {L.export_xlsx ?? ''}
          </Button>
          <Button size="sm" variant="outline" onClick={() => void downloadMcpScheduleTypst(mcpId!, mcpName, effectiveRevId)}
            title={L.export_schedule_typst ?? ''}>
            <FileType className="w-3.5 h-3.5" />
            {L.export_schedule_typst ?? ''}
          </Button>

          <span className="w-px self-stretch bg-border shrink-0" />

          <Select
            value={effectiveRevId ?? undefined}
            onValueChange={(next) => {
              setCompareRevId(next, mcpId ?? null);
              // A "vs" pick that now equals the new base (a self-compare)
              // is meaningless — clear it. Otherwise keep it: switching
              // the base shouldn't silently reset an otherwise-valid
              // second pick.
              if (effectiveAgainstRevId === next) setCompareAgainstRevId(null);
            }}
            disabled={isLoading || revs.length === 0}
          >
            <SelectTrigger className="h-8 text-xs w-[160px]">
              <SelectValue placeholder={L.version_pick_placeholder ?? ''} />
            </SelectTrigger>
            <SelectContent>
              {revs.map((r) => (
                <SelectItem key={r.rev_sid} value={r.rev_sid}>{identityLine(r, L)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground shrink-0">{L.compare_vs_label ?? 'vs'}</span>
          <Select
            value={effectiveAgainstRevId ?? CURRENT_SENTINEL}
            onValueChange={(v) => setCompareAgainstRevId(v === CURRENT_SENTINEL ? null : v)}
            disabled={!effectiveRevId}
          >
            <SelectTrigger className="h-8 text-xs w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CURRENT_SENTINEL}>{L.compare_vs_current_option ?? ''}</SelectItem>
              {againstOptions.map((r) => (
                <SelectItem key={r.rev_sid} value={r.rev_sid}>{identityLine(r, L)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Diff-impact badge and the PDF/Schedule/Typst dropdown menu
              both removed: the badge was redundant with Watch's own
              comparing view, and with only schedule_typst/schedule_xlsx
              left as real export targets (PDF/mcp_schedule deprecated),
              a dropdown for one remaining item was unneeded indirection
              — both now flat buttons above, unconditional. */}
        </>
      )}
    </div>
  );
}
